import { Dispatch, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { Action } from 'redux';

import { UTC_MOMENT_FORMAT } from '../../lib/constants/date-time.constants';
import { CurrencyEnum } from '../../lib/enums';
import { Earning } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { searchEarnings as searchEarningsData } from '../../lib/store/fundamental';
import { FundamentalCall, SearchEarningsPayload } from '../../lib/store/fundamental/types';
import { getCurrencySymbolByCurrencyCode } from '../../lib/util/DataHelpers';
import { earningsCalendarInitialDate } from '../../lib/util/DateTimeHelpers';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import UpcomingEarnings from '../../shared_components/UpcomingEarnings/UpcomingEarnings';
import { EarningRow } from '../../shared_components/UpcomingEarnings/UpcomingEarnings.types';
import { UI_ROUTES } from '../../util/routes';

const UPCOMING_EARNINGS_ITEMS_COUNT = 4;
const EARNINGS_DEFAULT_FILTER_DAYS_COUNT = 30;
const DEFAULT_EARNINGS_END_OFFSET_DAYS = EARNINGS_DEFAULT_FILTER_DAYS_COUNT - 1;

const UpcomingEarningsWrapper = (props: ReduxProps) => {
  const {
    earnings,
    companyLogoAndName,
    isGettingEarning,
    searchEarnings,
  } = props;
  const navigate = useNavigate();
  const range = useMemo(() => {
    const startDate = earningsCalendarInitialDate();
    const endDate = moment(startDate).add(DEFAULT_EARNINGS_END_OFFSET_DAYS, 'day').toDate();

    return {
      startDate: moment(startDate).format(UTC_MOMENT_FORMAT),
      endDate: moment(endDate).format(UTC_MOMENT_FORMAT),
      key: 'selection',
    };
  }, []);

  const topEarnings = useMemo((): EarningRow[] => (
    earnings.content
      .filter((item: Earning) => item.date >= range.startDate && item.date <= range.endDate)
      .sort((firstItem: Earning, secondItem: Earning) => {
        if (firstItem.date === secondItem.date) {
          return firstItem.time?.localeCompare(secondItem.time);
        }

        return firstItem.date?.localeCompare(secondItem.date);
      })
      .slice(0, UPCOMING_EARNINGS_ITEMS_COUNT)
      .map((item: Earning) => ({
        ...item,
        // Single market: dollars unless the response names a currency.
        currencySymbol: getCurrencySymbolByCurrencyCode(item.currency ?? CurrencyEnum.USD),
        logo: companyLogoAndName[item.symbol]?.logo ?? '',
        url: companyLogoAndName[item.symbol]?.url ?? '',
        displayName: companyLogoAndName[item.symbol]?.name ?? '',
      }))
  ), [earnings.content, range.endDate, range.startDate, companyLogoAndName]);

  useEffect(() => {
    searchEarnings({
      startDate: range.startDate,
      endDate: range.endDate,
      // Must match the calendar page's sort — both fetches write into the same earningsSearchResponse.
      sortAscending: true,
    });
  }, [range.endDate, range.startDate, searchEarnings]);

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`, { state: { range } });
  };

  const handleViewAllClick = () => {
    navigate(UI_ROUTES.discover + UI_ROUTES.earningsCalendar, { state: range });
  };

  return (
    <UpcomingEarnings
      earnings={topEarnings}
      isLoading={isGettingEarning}
      onViewAllClick={handleViewAllClick}
      onSymbolClick={handleSymbolClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  earnings: state.fundamental.earningsSearchResponse,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  isGettingEarning: isCallStatusPending(FundamentalCall.searchEarnings, state.fundamental.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  searchEarnings: (payload: SearchEarningsPayload) => dispatch(searchEarningsData(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(UpcomingEarningsWrapper);
